/**
 * BootstrapProvider — single fetch for tenant branding, feature flags and the
 * sidebar.
 *
 * Replaces separate /branding and /features calls on every app load.
 * Branding is applied progressively (default theme first); features hydrate
 * the same cache previously owned by FeaturesProvider.
 */
import React from 'react';
import { config } from '../config';
import { fetchBootstrap } from '../services/bootstrapService';
import type { FeatureRead } from '../services/featuresService';
import type { TenantBranding } from '../types/branding';
import type { TenantCurrency } from '../types/currency';
import type { NavSection } from '../navigation/types';

const DEFAULT_CURRENCY: TenantCurrency = { code: 'USD', symbol: '$' };

interface BootstrapContextValue {
  branding: TenantBranding | null;
  features: FeatureRead[];
  currency: TenantCurrency;
  /** The sidebar the server built for this workspace (backend/services/navigation). */
  navigation: NavSection[];
  /** This workspace's board of each spec: spec key -> dashboard id. */
  boards: Record<string, string>;
  /** True only until the first bootstrap fetch settles for the current tenant. */
  loading: boolean;
  error: string | null;
  refreshBootstrap: () => Promise<void>;
  refreshFeatures: () => Promise<void>;
  setFeatureLocal: (updated: FeatureRead) => void;
}

const BootstrapContext = React.createContext<BootstrapContextValue>({
  branding: null,
  features: [],
  currency: DEFAULT_CURRENCY,
  navigation: [],
  boards: {},
  loading: true,
  error: null,
  refreshBootstrap: async () => {},
  refreshFeatures: async () => {},
  setFeatureLocal: () => {},
});

function readTenantIdFromStorage(): string | null {
  try {
    const raw = window.localStorage.getItem('klaris_tenant');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const id = parsed?.tenant_id;
    return id ? String(id) : null;
  } catch {
    return null;
  }
}

function applyBrandingSideEffects(branding: TenantBranding | null) {
  if (branding?.name) {
    document.title = branding.name;
  }
  const faviconHref = branding?.faviconUrl || branding?.logoUrl;
  if (faviconHref) {
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
    if (link) {
      link.href = faviconHref;
    } else {
      const newLink = document.createElement('link');
      newLink.rel = 'icon';
      newLink.href = faviconHref;
      document.head.appendChild(newLink);
    }
  }
}

function resetBrandingSideEffects() {
  document.title = config.brandName;
  const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
  if (link) {
    link.href = config.faviconUrl;
  }
}

let globalResetBootstrap: (() => void) | null = null;

export function resetBootstrapOnLogout() {
  globalResetBootstrap?.();
}

export function useBootstrap(): BootstrapContextValue {
  return React.useContext(BootstrapContext);
}

function readTokenFromStorage(): string | null {
  try {
    return window.localStorage.getItem('klaris_jwt');
  } catch {
    return null;
  }
}

export default function BootstrapProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = React.useState<TenantBranding | null>(null);
  const [features, setFeatures] = React.useState<FeatureRead[]>([]);
  const [currency, setCurrency] = React.useState<TenantCurrency>(DEFAULT_CURRENCY);
  const [navigation, setNavigation] = React.useState<NavSection[]>([]);
  const [boards, setBoards] = React.useState<Record<string, string>>({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [tenantId, setTenantId] = React.useState<string | null>(readTenantIdFromStorage);
  const [token, setToken] = React.useState<string | null>(readTokenFromStorage);

  // Reload on a new sign-in, not only on a new workspace. Someone whose old
  // session had run out, signing back in to the same workspace, kept the
  // failed load from before they signed in — and, with the sidebar now coming
  // from here, an empty sidebar until they refreshed the page.
  React.useEffect(() => {
    const checkTenant = () => {
      const next = readTenantIdFromStorage();
      setTenantId((prev) => (prev === next ? prev : next));
      const nextToken = readTokenFromStorage();
      setToken((prev) => (prev === nextToken ? prev : nextToken));
    };
    checkTenant();
    const interval = setInterval(checkTenant, 200);
    return () => clearInterval(interval);
  }, []);

  const loadBootstrap = React.useCallback(
    async (signal?: AbortSignal) => {
      if (!token || !tenantId) {
        setBranding(null);
        setFeatures([]);
        setCurrency(DEFAULT_CURRENCY);
        setNavigation([]);
        setBoards({});
        setError(null);
        setLoading(false);
        return;
      }

      try {
        const data = await fetchBootstrap(tenantId);
        if (signal?.aborted) return;
        setBranding(data.branding);
        setFeatures(data.features);
        setCurrency(data.currency ?? DEFAULT_CURRENCY);
        setNavigation(data.navigation ?? []);
        setBoards(data.boards ?? {});
        setError(null);
        applyBrandingSideEffects(data.branding);
      } catch (err) {
        if (signal?.aborted) return;
        const msg = err instanceof Error ? err.message : 'Failed to load bootstrap';
        setError(msg);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [tenantId, token],
  );

  React.useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    loadBootstrap(ctrl.signal);
    return () => ctrl.abort();
  }, [loadBootstrap]);

  const refreshBootstrap = React.useCallback(async () => {
    await loadBootstrap();
  }, [loadBootstrap]);

  // The sidebar is built from the same switches, so re-reading them re-reads
  // it too: after a failed toggle, when what the server holds is not known.
  const refreshFeatures = React.useCallback(async () => {
    await loadBootstrap();
  }, [loadBootstrap]);

  const setFeatureLocal = React.useCallback((updated: FeatureRead) => {
    setFeatures((prev) => {
      const idx = prev.findIndex((f) => f.key === updated.key);
      if (idx === -1) return [...prev, updated];
      const next = prev.slice();
      next[idx] = updated;
      return next;
    });
    // The sidebar is built on the server from these same switches, so a row
    // that depends on this one only appears or goes once it is rebuilt.
    void loadBootstrap();
  }, [loadBootstrap]);

  React.useEffect(() => {
    globalResetBootstrap = () => {
      setBranding(null);
      setFeatures([]);
      setCurrency(DEFAULT_CURRENCY);
      setNavigation([]);
      setBoards({});
      setError(null);
      setLoading(false);
      resetBrandingSideEffects();
    };
    return () => {
      globalResetBootstrap = null;
    };
  }, []);

  const value = React.useMemo<BootstrapContextValue>(
    () => ({
      branding,
      features,
      currency,
      navigation,
      boards,
      loading,
      error,
      refreshBootstrap,
      refreshFeatures,
      setFeatureLocal,
    }),
    [branding, features, currency, navigation, boards, loading, error, refreshBootstrap, refreshFeatures, setFeatureLocal],
  );

  return <BootstrapContext.Provider value={value}>{children}</BootstrapContext.Provider>;
}

/** Read-only access to the full feature list. */
export function useFeatures(): BootstrapContextValue {
  return useBootstrap();
}

/** Tenant currency (code + display symbol), backed by the bootstrap cache. Defaults to USD/"$". */
export function useCurrency(): TenantCurrency {
  const { currency } = useBootstrap();
  return currency;
}

/** The workspace's sidebar, as the server built it. */
export function useNavigation(): NavSection[] {
  return useBootstrap().navigation;
}

/** This workspace's board of a spec (e.g. `sales_scorecard`), or null if it has none. */
export function useBoardId(specKey: string): string | null {
  return useBootstrap().boards[specKey] ?? null;
}

/** Single-feature gate backed by the bootstrap cache. */
export function useFeature(key: string): {
  enabled: boolean;
  loading: boolean;
  feature: FeatureRead | undefined;
  config: Record<string, any>;
} {
  const { features, loading } = useBootstrap();
  const feature = features.find((f) => f.key === key);
  return {
    enabled: Boolean(feature?.enabled),
    loading,
    feature,
    config: feature?.config ?? {},
  };
}
